import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import ScrollIndicator from '../../components/ScrollIndicator';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import './Home.css';

const Home = (): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix: 'home' });
  const { t: tProducts } = useTranslation('translation', { keyPrefix: 'products' });
  const { t: tSupport } = useTranslation('translation', { keyPrefix: 'support' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const handleIntersection = useCallback((entry: IntersectionObserverEntry) => {
    if (entry.isIntersecting) {
      entry.target.classList.add('visible');
    }
  }, []);

  const observerOptions = {
    threshold: 0.2,  // 要素の20%が見えるまで待つ
    rootMargin: '-100px 0px'  // 上下100pxのマージンを設定（要素が画面内により入ってから表示）
  };

  const aboutTitleRef = useIntersectionObserver<HTMLHeadingElement>(handleIntersection, observerOptions);
  const aboutTextRef = useIntersectionObserver<HTMLParagraphElement>(handleIntersection, observerOptions);
  const productsTitleRef = useIntersectionObserver<HTMLHeadingElement>(handleIntersection, observerOptions);
  const productsTextRef = useIntersectionObserver<HTMLParagraphElement>(handleIntersection, observerOptions);
  const supportTitleRef = useIntersectionObserver<HTMLHeadingElement>(handleIntersection, observerOptions);
  const supportTextRef = useIntersectionObserver<HTMLParagraphElement>(handleIntersection, observerOptions);
  const sideText1Ref = useIntersectionObserver<HTMLParagraphElement>(handleIntersection, observerOptions);
  const sideText2Ref = useIntersectionObserver<HTMLParagraphElement>(handleIntersection, observerOptions);
  const supportCard1Ref = useIntersectionObserver<HTMLDivElement>(handleIntersection, observerOptions);
  const supportCard2Ref = useIntersectionObserver<HTMLDivElement>(handleIntersection, observerOptions);
  const supportCard3Ref = useIntersectionObserver<HTMLDivElement>(handleIntersection, observerOptions);

  const featuredProducts = [
    {
      id: 'keel_za',
      name: tProducts('original.items.keel_za.name'),
      size: tProducts('original.items.keel_za.size'),
      description: tProducts('original.items.keel_za.description'),
      price: tProducts('original.items.keel_za.price'),
      image: '/images/scissors/keel_za.png',
    },
    {
      id: 'calis_xa',
      name: tProducts('original.items.calis_xa.name'),
      size: tProducts('original.items.calis_xa.size'),
      description: tProducts('original.items.calis_xa.description'),
      price: tProducts('original.items.calis_xa.price'),
      image: '/images/scissors/calis_xa.png',
    },
    {
      id: 'zwxa',
      name: tProducts('thinning.items.zwxa.name'),
      size: tProducts('thinning.items.zwxa.size'),
      description: tProducts('thinning.items.zwxa.description'),
      price: tProducts('thinning.items.zwxa.price'),
      image: '/images/scissors/zwxa.png',
    },
  ];

  const services = [
    {
      title: t('support.services.salon_real_estate.title'),
      description: t('support.services.salon_real_estate.description'),
      image: '/images/support/support_01.jpg',
    },
    {
      title: t('support.services.tech_support.title'),
      description: t('support.services.tech_support.description'),
      image: '/images/support/support_02.jpg',
    },
    {
      title: t('support.services.matterport.title'),
      description: t('support.services.matterport.description'),
      image: '/images/support/support_04.jpg',
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const images = document.querySelectorAll('#home .image-container img');
      
      images.forEach((image) => {
        const rect = image.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (rect.top < windowHeight && rect.bottom > 0) {
          const scrolled = windowHeight - rect.top;
          const scrollPercent = (scrolled / windowHeight);
          const moveAmount = 50 - (scrollPercent * 50 * 2.5);
          (image as HTMLElement).style.transform = `translateY(${moveAmount}px)`;
        }
      });

      const productCards = document.querySelectorAll('.product-card');
      productCards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (rect.top < windowHeight && rect.bottom > 0) {
          const scrolled = windowHeight - rect.top;
          const cardHeight = rect.height;
          const totalScrollDistance = windowHeight + cardHeight;
          const scrollPercent = Math.min(Math.max(scrolled / totalScrollDistance, 0), 1);
          
          const image = card.querySelector('.product-image') as HTMLElement;
          if (image) {
            const scaleProgress = Math.min(scrollPercent / 0.4, 1);
            const scale = Math.max(2.5 - (scaleProgress * 1.5), 1.2);
            const moveAmount = scrollPercent * 100;

            // 回転の制御（15度から0度まで）
            const rotationProgress = Math.min(scrollPercent / 0.6, 1); // 60%スクロールで回転完了
            const rotation = 15 - (rotationProgress * 15); // 15度から0度まで

            // 透明度の制御（最初の30%のスクロールで不透明に）
            const opacityProgress = Math.min(scrollPercent / 0.3, 1);
            const opacity = opacityProgress;
            
            image.style.transform = `translateY(${moveAmount}px) scale(${scale}) rotate(${rotation}deg)`;
            image.style.opacity = opacity.toString();
          }

          // infoの表示制御
          const info = card.querySelector('.product-info') as HTMLElement;
          if (info) {
            if (scrollPercent > 0.3) {
              info.classList.add('visible');
            } else {
              info.classList.remove('visible');
            }
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="home" id="home">
      <section className="hero">
        <ScrollIndicator />
      </section>

      <section>
        <div className="section-container left">
          <h1 className="section-title" ref={aboutTitleRef}>{t('about.title')}</h1>
          <p className="section-text" ref={aboutTextRef}>{t('about.description')}</p>
        </div>
      </section>

      <section className="image-section">
        <div className="image-container left">
          <img src="/images/image_01.jpg" alt="武芸シザー" />
          <div className="image-text-container">
            <div className="vertical-line"></div>
            <p className="image-side-text" ref={sideText1Ref}>what types of scissors are there?</p>
          </div>
        </div>
      </section>

      <section>
        <div className="section-container left">
          <h1 className="section-title" ref={productsTitleRef}>{tProducts('title')}</h1>
          <p className="section-text" ref={productsTextRef}>{tProducts('description')}</p>
          
          <div className="products-card-container">
            {featuredProducts.map((product) => (
              <div className="product-card" key={product.id}>
                <div className="product-image-container">
                  <img className="product-image" src={product.image} alt={product.name} />
                </div>
                <div className="product-info">
                  <h3 className="product-name">{product.name}</h3>
                  <div className="product-sizes">
                    {Array.isArray(product.size)
                      ? product.size.map((size) => (
                        <span key={size} className="product-size">{size}</span>
                      ))
                      : <span className="product-size">{product.size}</span>
                    }
                  </div>
                  <p className="product-description">{product.description}</p>
                  <div className="product-price">{product.price}<span className="product-price-tax">{tCommon('tax')}</span></div>
                </div>
              </div>
            ))}
          </div>
          <div className="view-more-container">
            <Link to="/products" className="view-more-button">{tCommon('more')}</Link>
          </div>
        </div>
      </section>

      <section className="image-section">
        <div className="image-container right">
          <div className="image-text-container">
            <p className="image-side-text" ref={sideText2Ref}>There are many other things we can do...</p>
            <div className="vertical-line"></div>
          </div>
          <img src="/images/image_03.jpg" alt="武芸シザー" />
        </div>
      </section>

      <section>
        <div className="section-container left">
          <h1 className="section-title" ref={supportTitleRef}>{tSupport('title')}</h1>
          <p className="section-text" ref={supportTextRef}>{tSupport('description')}</p>
          <div className="support-grid">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="support-card"
                ref={index === 0 ? supportCard1Ref : index === 1 ? supportCard2Ref : supportCard3Ref}
              >
                <div className="support-card-image-container">
                  <img className="support-card-image" src={service.image} alt={service.title} />
                </div>
                <div className="support-card-content">
                  <h3 className="support-card-title">{service.title}</h3>
                  <p className="support-card-description">{service.description}</p>
                  <a href="#" className="support-card-link">{tCommon('more')}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 